import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';
import { Section } from '@wds/section';
import { Button } from '@wds/button';

import { ProgressCircle } from '@wds/progress-circle';
import { Toast } from '@wds/toast';
import { Table } from '@wds/table';

import PropTypes from 'prop-types';

import DisconnectAccountModal from '../../modals/DisconnectAccountModal';
import CustomField from '../../../../sma.common/components/react/CustomField/CustomField';
import BecomeApplicantModal from '../../modals/BecomeApplicantModal';

import EligibilityTab from './EligibilityTab';
import MyProfileTab from './MyProfileTab';
import UserSectionTab from './UserSectionTab';
import NotificationSettingsTab from './NotificationSettingsTab';

import STRINGS from '../../../constants';

const AccountSettingsPage = (props) => {
	const {
		selectedTab,
		csrfToken,
		languages,
		timezones,
		usesSso,
		assumedRole,
		siteInfo,
		userDetails,
		tabErrors,
		setTabErrors,
	} = props;
	const [settings, setSettings] = useState({});
	const [showDisconnectAccountModal, setShowDisconnectAccountModal] = useState(false);
	const [showBecomeApplicantModal, setShowBecomeApplicantModal] = useState(false);
	const [toastInfo, setToastInfo] = useState({ title: null, text: null, show: false, color: null });
	const [customFields, setCustomFields] = useState({});
	const [loadingCustomFields, setLoadingCustomFields] = useState(false);
	const [showCustomFields, setShowCustomFields] = useState(false);
	const [customFieldErrors, setCustomFieldErrors] = useState({});
	const [canSubmit, setCanSubmit] = useState(false);
	const [reset, setReset] = useState(false);
	const [editingFields, setEditingFields] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [notifications, setNotifications] = useState({});
	const [showBecomeApplicantWarning, setShowBecomeApplicantWarning] = useState(false);

	useEffect(() => {
		getUserSettings();
	}, []);

	useEffect(() => {
		if (settings?.custom_fields) {
			getCustomFields();
		}
		if (Object.keys(settings).some((key) => key.startsWith('notify_'))) {
			setNotifications(getNotificationSettings(settings));
		}
	}, [settings]);

	useEffect(() => {
		if (customFields.applicant || customFields.reviewer || customFields.recommender) {
			setShowCustomFields(true);
		} else {
			setShowCustomFields(false);
		}
		setLoadingCustomFields(false);
	}, [customFields]);

	useEffect(() => {
		const noCustomFieldErrors = Object.keys(customFieldErrors).length === 0;
		if (!noCustomFieldErrors) {
			setTabErrors({ ...tabErrors, userSectionTab: true });
		} else {
			const newTabErrors = { ...tabErrors };
			delete newTabErrors.userSectionTab;
			setTabErrors(newTabErrors);
		}
		const noEditing = Object.keys(editingFields).length === 0;
		const submittable = noCustomFieldErrors && noEditing && !isLoading;
		setCanSubmit(submittable);
	}, [customFieldErrors, editingFields, isLoading]);

	const handleReset = () => {
		setCustomFieldErrors({});
		getUserSettings();
		setReset(true);
		setTimeout(() => {
			setReset(false);
		}, 100);
		setShowBecomeApplicantWarning(false);
	};

	const {
		CustomerSupportText,
		needHelp,
		userSectionTab,
		visitHelpCenter,
	} = STRINGS.accountSettingsPage;

	const getNotificationSettings = (data) => {
		const tempNotifications = {};
		Object.keys(data).forEach((key) => {
			if ((key !== 'notify_direct_messages') && key.startsWith('notify_')) {
				const role = key.split('_')[1];
				if (!tempNotifications[key.split('_')[1]]) {
					tempNotifications[key.split('_')[1]] = {};
				}
				tempNotifications[role][key] = data[key];
			}
		});
		return tempNotifications;
	};

	const getCustomFields = () => {
		setLoadingCustomFields(true);
		const results = {};
		settings.roles.forEach((role) => {
			if (settings?.custom_fields && settings.custom_fields[role]) {
				settings.custom_fields[role].forEach((customField) => {
					if (!results[role]) {
						results[role] = [];
					}
					const values = {
						id: customField.id,
						name: customField.name,
						type: customField.type,
						required: customField.required,
						readonly: customField.readonly,
						value: customField.value,
					};
					if (customField.type === 1) {
						values.choices = customField.choices;
					}
					results[role].push(values);
				});
			}
		});
		if (Object.keys(results).length > 0) {
			setCustomFields(results);
		}
	};

	const getUserSettings = () => {
		const url = reverse('api-internal-user');
		const request = {
			method: 'GET',
			headers: { Accept: 'application/json' },
		};
		fetch(url, request)
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					return response.json();
				}
			})
			.then((data) => {
				data.current_password = '';
				data.new_password = '';
				setSettings(data);
			})
			.catch((error) => {
				setToastInfo({
					show: true,
					color: 'error',
					title: STRINGS.toast.error,
					text: STRINGS.toast.unknownError,
				});
			});
	};

	const handleChangeCheckbox = (key) => (e) => {
		const newSettings = { ...settings };
		newSettings[key] = !e.target.checked;
		if (key === 'email_subscribed') {
			newSettings.notify_direct_messages = !e.target.checked;
			Object.keys(notifications).forEach((role) => {
				Object.keys(notifications[role]).forEach((name) => {
					newSettings[name] = !e.target.checked;
				});
			});
		}
		setSettings(newSettings);
	};

	const handleChangeNotification = (name) => (e) => {
		const updatedSettings = { ...settings };
		updatedSettings[name] = e.target.checked;
		setSettings(updatedSettings);
	};

	const handleCustomFieldChange = (id) => (value) => {
		const updatedCustomFields = { ...customFields };
		Object.keys(updatedCustomFields).forEach((role) => {
			if (!updatedCustomFields[role]) {
				updatedCustomFields[role] = {};
			}
			updatedCustomFields[role].forEach((field) => {
				if (field.id === id) {
					field.value = value;
				}
			});
		});
		setCustomFields(updatedCustomFields);
	};

	const handleDisconnectGoogleSignOn = () => {
		setShowDisconnectAccountModal(true);
	};

	const handleOnDisconnectAccount = (password) => {
		setIsLoading(true);
		const url = reverse('api-internal-user');
		const refererUrl = reverse('account');
		const body = {
			social: {},
			new_password: password,
		};
		const request = {
			method: 'PATCH',
			headers: {
				'X-CSRFToken': csrfToken,
				Referer: refererUrl,
			},
			body: JSON.stringify(body),
		};
		fetch(url, request).then((response) => {
			if (response.status === 200) {
				response.json().then((data) => {
					if (assumedRole && Object.keys(assumedRole).length > 0) {
						delete data.current_password;
						delete data.new_password;
					}
					setSettings(data);
					setToastInfo({
						show: true,
						color: 'success',
						title: STRINGS.toast.success,
						text: STRINGS.toast.accountUpdated,
					});
				});
			} else if (response.status === 400) {
				response.json().then((data) => {
					if (data.custom_fields) {
						setCustomFieldErrors(data.custom_fields);
						setToastInfo({
							show: true,
							color: 'error',
							title: STRINGS.toast.error,
							text: STRINGS.toast.customFieldErrors,
						});
					}
				});
				setToastInfo({
					show: true,
					color: 'error',
					title: STRINGS.toast.error,
					text: STRINGS.toast.accountNotUpdated,
				});
			}
		}).catch(() => {
			setToastInfo({
				show: true,
				color: 'error',
				title: STRINGS.toast.error,
				text: STRINGS.toast.accountNotUpdated,
			});
		}).finally(() => {
			setIsLoading(false);
			handleReset();
		});
	};

	const handleBecomeApplicant = (requiredFields) => {
		const updatedSettings = { ...settings };
		updatedSettings.roles.push('applicant');
		updatedSettings.custom_fields.applicant = requiredFields;
		setSettings(updatedSettings);
		setShowBecomeApplicantWarning(true);
		setShowBecomeApplicantModal(false);
	};

	const handleOpenBecomeApplicantModal = () => {
		setShowBecomeApplicantModal(true);
	};

	const handleSetupOrganization = () => {
		window.location.href = reverse('register-organization');
	};

	const handleEditEligibility = () => {
		window.location.href = reverse('applicant-eligibility-edit');
	};

	const convertNotifications = (body) => {
		Object.keys(notifications).forEach((role) => {
			Object.keys(notifications[role]).forEach((key) => {
				body[key] = settings[key];
			});
		});
	};

	const getBody = (accountSettings) => {
		const updatableCustomFields = {};
		Object.keys(customFields).forEach((role) => {
			updatableCustomFields[role] = customFields[role].filter((field) => !field.readonly);
		});
		const body = {
			email: accountSettings?.email,
			first_name: accountSettings?.first_name,
			last_name: accountSettings?.last_name,
			language_preference: accountSettings?.language_preference,
			timezone: accountSettings?.timezone,
			custom_fields: updatableCustomFields,
			email_subscribed: accountSettings?.email_subscribed,
			notify_direct_messages: accountSettings?.notify_direct_messages,
			roles: accountSettings?.roles,
		};

		if (accountSettings?.current_password?.length > 0) {
			body.current_password = accountSettings?.current_password;
			body.new_password = accountSettings?.new_password;
		}

		convertNotifications(body);
		return body;
	};

	const handleOnSave = () => {
		setIsLoading(true);
		const url = reverse('api-internal-user');
		const refererUrl = reverse('account');
		const body = getBody(settings);
		const request = {
			method: 'PATCH',
			headers: {
				'X-CSRFToken': csrfToken,
				Referer: refererUrl,
			},
			body: JSON.stringify(body),
		};
		fetch(url, request).then((response) => {
			if (response.status === 200) {
				response.json().then((data) => {
					if (assumedRole && Object.keys(assumedRole).length > 0) {
						delete data.current_password;
						delete data.new_password;
					}
					setSettings(data);
					setToastInfo({
						show: true,
						color: 'success',
						title: STRINGS.toast.success,
						text: STRINGS.toast.accountUpdated,
					});
				});
			} else if (response.status === 400) {
				response.json().then((data) => {
					if (data.custom_fields) {
						setCustomFieldErrors(data.custom_fields);
						setToastInfo({
							show: true,
							color: 'error',
							title: STRINGS.toast.error,
							text: STRINGS.toast.customFieldErrors,
						});
					} else if (data.current_password) {
						setToastInfo({
							show: true,
							color: 'error',
							title: STRINGS.toast.error,
							text: STRINGS.toast.invalidPassword,
						});
					} else {
						setToastInfo({
							show: true,
							color: 'error',
							title: STRINGS.toast.error,
							text: STRINGS.toast.accountNotUpdated,
						});
					}
				});
			}
		}).catch(() => {
			setToastInfo({
				show: true,
				color: 'error',
				title: STRINGS.toast.error,
				text: STRINGS.toast.accountNotUpdated,
			});
		}).finally(() => {
			setIsLoading(false);
			handleReset();
		});
	};

	const handleOnCancel = () => {
		getUserSettings();
		setToastInfo({
			show: true,
			color: 'success',
			title: STRINGS.toast.success,
			text: STRINGS.toast.accountSettingsReset,
		});
	};

	const cancelEmailChangeRequest = () => {
		const url = reverse('cancel-email-change');
		const request = {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'X-CSRFToken': csrfToken,
			},
		};
		fetch(url, request).then((response) => {
			if (response.status === 200) {
				response.json().then((data) => {
					setToastInfo({
						show: true,
						color: data.success ? 'success' : 'error',
						title: data.success ? STRINGS.toast.success : STRINGS.toast.error,
						text: data.message,
					});
				});
			}
		}).catch(() => {
			setToastInfo({
				show: true,
				color: 'error',
				title: STRINGS.toast.error,
				text: STRINGS.toast.unknownError,
			});
		});
		setSettings(
			{
				...settings,
				temp_email: '',
			},
		);
	};

	const resendEmailChangeRequest = () => {
		const url = reverse('resend-email-confirmation');
		const request = {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'X-CSRFToken': csrfToken,
			},
		};
		fetch(url, request).then((response) => {
			if (response.status === 200) {
				response.json().then((data) => {
					setToastInfo({
						show: true,
						color: data.success ? 'success' : 'error',
						title: data.success ? STRINGS.toast.success : STRINGS.toast.error,
						text: data.message,
					});
				});
			}
		}).catch(() => {
			setToastInfo({
				show: true,
				color: 'error',
				title: STRINGS.toast.error,
				text: STRINGS.toast.unknownError,
			});
		});
	};

	const renderCustomFields = useCallback(() => (
		Object.keys(customFields).map((role) => (
			<Box id={`custom-fields-${role}`} key={`custom-fields-${role}`} role='region'>
				{customFields[role].map((field) => (
					<Box key={field.id}>
						<CustomField
							onChange={handleCustomFieldChange(field.id)}
							key={`custom-field-${field.id}`}
							id={field.id}
							label={field.name}
							value={field.value}
							name={field.name}
							type={field.type}
							required={field.required}
							readOnly={field.readonly}
							choices={field.choices}
							errors={customFieldErrors}
							setErrors={setCustomFieldErrors}
							helperText={field.helper_text ? field.helper_text : ''}
							defaultValue={field.default}
							aria-required={field.required}
							aria-invalid={!!customFields[field.id]}
							aria-describedby={customFieldErrors[field.id] ? `${field.id}-error` : undefined}
						/>
					</Box>
				))}
			</Box>
		))
	), [customFields, customFieldErrors, handleCustomFieldChange, setCustomFieldErrors]);

	const renderOrganizations = useCallback(() => (
		settings?.organizations.map((organization) => (
			<Table.Row key={organization.id}>
				<Table.Cell>
					<Typography lineHeight={2}>{organization.name}</Typography>
				</Table.Cell>
				<Table.Cell>
					<Typography
						lineHeight={2}
						align='center'
					>
						{userSectionTab[organization.role]}
					</Typography>
				</Table.Cell>
			</Table.Row>
		))
	), [settings]);

	const usesGoogleSSO = (!settings.social || (settings?.social && Object.keys(settings.social).length > 0));

	return (
		<>
			<Box style={{ marginBottom: '100px' }}>
				<Grid p={2} mb={1}>
					<Grid.Item>{null}</Grid.Item>
					<Grid.Item xs={7}>
						<Box id='my-profile' display={selectedTab === 'myProfileTab' ? '' : 'none'}>
							<MyProfileTab
								settings={settings}
								setSettings={setSettings}
								languages={languages}
								timezones={timezones}
								cancelEmailChangeRequest={cancelEmailChangeRequest}
								resendEmailChangeRequest={resendEmailChangeRequest}
								handleDisconnectGoogleSignOn={handleDisconnectGoogleSignOn}
								reset={reset}
								editingFields={editingFields}
								setEditingFields={setEditingFields}
								assumedRole={assumedRole}
								usesSso={usesSso}
								usesGoogleSSO={usesGoogleSSO}
								setToastInfo={setToastInfo}
								isSuperuser={userDetails?.isSuperuser}
							/>
						</Box>
						<Box
							id='user-section-settings'
							display={selectedTab === 'userSectionTab' ? '' : 'none'}
						>
							<UserSectionTab
								settings={settings}
								siteInfo={siteInfo}
								handleOpenBecomeApplicantModal={handleOpenBecomeApplicantModal}
								handleSetupOrganization={handleSetupOrganization}
								renderCustomFields={renderCustomFields}
								renderOrganizations={renderOrganizations}
								showCustomFields={showCustomFields}
								showBecomeApplicantWarning={showBecomeApplicantWarning}
								loadingCustomFields={loadingCustomFields}
								selectedTab={selectedTab}
							/>
						</Box>
						<Box
							id='notification-settings'
							display={selectedTab === 'notificationSettingsTab' ? '' : 'none'}
						>
							<NotificationSettingsTab
								settings={settings}
								notifications={notifications}
								handleChangeNotification={handleChangeNotification}
								handleChangeCheckbox={handleChangeCheckbox}
							/>
						</Box>
						<Box
							id='user-eligibility'
							display={selectedTab === 'userEligibilityTab' ? '' : 'none'}
						>
							<EligibilityTab
								settings={settings}
								siteInfo={siteInfo}
								userDetails={userDetails}
								handleEditEligibility={handleEditEligibility}
							/>
						</Box>
					</Grid.Item>
					<Grid.Item xs={3}>
						<Box
							className='sma-card-content'
							id='need-help-card'
							role='region'
							aria-labelledby='section-label'
						>
							<Section>
								<Box px={8}>
									<Typography id='section-label' variant='bodySm' weight='medium'>
										{needHelp}
									</Typography>
								</Box>
								<hr />
								<Box>
									<ul>
										<li>
											<Typography
												component='a'
												href={reverse('helpdesk')}
												color='link'
												id='customer-support'
											>
												{CustomerSupportText}
											</Typography>
										</li>
										<li>
											<Typography
												component='a'
												href={window?.LOCALE?.helpCenterUrl}
												color='link'
												id='help-center'
											>
												{visitHelpCenter}
											</Typography>
										</li>
									</ul>
								</Box>
							</Section>
						</Box>
					</Grid.Item>
				</Grid>
			</Box>
			<Box
				style={{ backgroundColor: '#ffffff', position: 'fixed', bottom: 90, width: '100%' }}
				borderColor='main'
				id='controls'
				aria-label='form controls'
				role='region'
			>
				<Box display='flex' justifyContent='center' my={4}>
					<Box display='flex' justifyContent='center' gap={4}>
						<Box>
							<Button
								color='muted'
								id='cancel-button'
								onClick={handleOnCancel}
								aria-label='cancel button'
							>
								{STRINGS.cancelBtn}
							</Button>
						</Box>
						<Box>
							<Button
								label='Save'
								color='primary'
								onClick={handleOnSave}
								disabled={!canSubmit}
								aria-label='save button'
							>
								{isLoading ? <ProgressCircle continuous size='sm' /> : STRINGS.saveBtn}
							</Button>
						</Box>
					</Box>
				</Box>
			</Box>
			<Toast
				autoClose
				color={toastInfo.color}
				dismissible
				onClose={() => setToastInfo({
					...toastInfo,
					title: null,
					text: null,
					show: false,
				})}
				show={toastInfo.show}
			>
				<Toast.Title>{toastInfo.title}</Toast.Title>
				<Toast.Body>{toastInfo.text}</Toast.Body>
			</Toast>
			<DisconnectAccountModal
				open={showDisconnectAccountModal}
				setOpen={setShowDisconnectAccountModal}
				handleOnSuccess={handleOnDisconnectAccount}
			/>
			<BecomeApplicantModal
				open={showBecomeApplicantModal}
				setOpen={setShowBecomeApplicantModal}
				handleOnSuccess={handleBecomeApplicant}
				customFields={customFields}
				setCustomFields={setCustomFields}
			/>
		</>
	);
};

AccountSettingsPage.propTypes = {
	selectedTab: PropTypes.string.isRequired,
	csrfToken: PropTypes.string.isRequired,
	languages: PropTypes.object.isRequired,
	timezones: PropTypes.array.isRequired,
	usesSso: PropTypes.bool.isRequired,
	assumedRole: PropTypes.object,
	siteInfo: PropTypes.object.isRequired,
	userDetails: PropTypes.object.isRequired,
	tabErrors: PropTypes.object.isRequired,
	setTabErrors: PropTypes.func.isRequired,
};

AccountSettingsPage.defaultProps = { assumedRole: {} };

export default AccountSettingsPage;
